import React from 'react';
import { getFeatureFlagValue } from '@citrite/feature-flags';
import type {
	AiAgentConfigContextProviderProps,
	IAiAgentConfigContext,
} from '@sharefiledev/safregistry-pilet-sdk';
import {
	AiAgentConfigAudience,
	AiAgentConfigValue,
	safRegistrySdk as sdk,
} from '../http';
import { getLogger } from '../logger';

const AiAgentConfigContext = React.createContext<IAiAgentConfigContext>({} as any);

export const AiAgentConfigContextProvider: React.FC<
	AiAgentConfigContextProviderProps
> = ({ agentNamespace, agentName, featureFlags, cacheTtlInMinutes, children }) => {
	const cacheTtl = cacheTtlInMinutes ? cacheTtlInMinutes * 60 : 5 * 60;
	const cacheKey = React.useMemo(
		() => `aiAgentConfig_${agentNamespace}_${agentName}`,
		[agentNamespace, agentName]
	);

	const [isLoading, setIsLoading] = React.useState(false);
	const [values, setValues] = React.useState<AiAgentConfigValue[]>([]);

	// Load values from session storage or API
	const loadValues = React.useCallback(async () => {
		setIsLoading(true);

		try {
			// Check session storage first
			const cachedData = sessionStorage.getItem(cacheKey);
			if (cachedData) {
				const { values: cachedValues, timestamp } = JSON.parse(cachedData);
				const now = Math.floor(Date.now() / 1000);

				// If cache is still valid, use cached values
				if (now - timestamp < cacheTtl) {
					setValues(cachedValues);
					setIsLoading(false);
					return;
				}
			}

			// Cache expired or doesn't exist, fetch from API
			const configValues = await sdk.listConfigValuesByAgent(
				AiAgentConfigAudience.Tenant,
				agentNamespace,
				agentName
			);

			const fetchedValues = configValues.resources || [];
			setValues(fetchedValues);

			// Save to session storage with current timestamp
			const cacheData = {
				values: fetchedValues,
				timestamp: Math.floor(Date.now() / 1000),
			};
			sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
		} catch (error) {
			getLogger().logError(`Error fetching agent config values: ${error}`);
		} finally {
			setIsLoading(false);
		}
	}, [agentNamespace, agentName, cacheKey, cacheTtl]);

	const getConfigValue = React.useCallback(
		(key: string): any | undefined => {
			if (!values || values.length === 0) {
				return undefined;
			}

			const config = values.find(item => item.key === key);
			return config ? config.value : undefined;
		},
		[values]
	);
	const isEnabled = React.useCallback(
		(key: string): boolean | undefined => {
			const value = getConfigValue(key);
			if (value === undefined) {
				return undefined;
			}
			if (typeof value === 'boolean') {
				return value;
			}
			if (typeof value === 'string') {
				return value.toLowerCase() === 'true';
			}
			if (typeof value === 'number') {
				return value !== 0;
			}
			return undefined;
		},
		[getConfigValue]
	);

	// Refresh function that forces a new API call and clears cache
	const refresh = React.useCallback(async () => {
		// Clear existing cache
		sessionStorage.removeItem(cacheKey);
		// Load fresh values
		await loadValues();
	}, [cacheKey, loadValues]);

	React.useEffect(() => {
		let ffEnabled = true;
		if (featureFlags && featureFlags.length > 0) {
			ffEnabled = featureFlags.every(ff => getFeatureFlagValue(ff));
		}

		if (ffEnabled) {
			loadValues();
		}
	}, [featureFlags, loadValues]);

	return (
		<AiAgentConfigContext.Provider
			value={{
				getConfigValue,
				isEnabled,
				isLoading,
				refresh,
			}}
		>
			{children}
		</AiAgentConfigContext.Provider>
	);
};

export function useAiAgentConfigContext() {
	return React.useContext(AiAgentConfigContext);
}
